import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Calendar, Link2, Clock, Flame, ChevronDown, ChevronUp, Filter, Activity, ClipboardList, Trash2, CheckSquare, Square, Upload, Settings, Star } from 'lucide-react';
import { toast } from 'sonner';
import { generateTcxFile } from '@/lib/tcxExport';
import TopNav from '@/components/TopNav';
import { BoatTypeIcon, getBoatById } from '@/components/BoatIcons';
import SessionBreakdown from '@/components/SessionBreakdown';
import WorkoutStrokesReplay from '@/components/WorkoutStrokesReplay';
import RaceSplitsCard from '@/components/RaceSplitsCard';
import RowExportMenu from '@/components/RowExportMenu';
import { ZoneTimeBar } from '@/components/ZoneTimeBar';
import { SessionMaxHr } from '@/components/SessionMaxHr';
import { maxHrInUse } from '@/lib/hrZones';
import { SessionAnalysisInline } from '@/pages/TrainingSessionAnalysis';
import {
  tcxPayloadFromWorkout,
  EXPORT_PLATFORMS,
  HR_ZONES,
  DETAIL_METRICS,
  SESSION_TYPE_BADGES,
  resolveSessionType,
  FILTER_WINDOWS,
  windowStart,
  formatDuration,
  formatDurationFull,
  formatDistance,
  formatDistanceMeters,
  formatSplit,
  formatDate,
  formatTime,
  getHRZones,
  isProgramWorkout,
  getWorkoutTitle,
} from '@/lib/history';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// On the water calories need an HR strap (Keytel); without one show "--".
const needsHrStrap = (w) => !w.is_pm5_session && w.app_mode !== 3 && !w.calories && !(w.hr_bpm_series || []).length;
const caloriesText = (w) => (needsHrStrap(w) ? '--' : Math.floor(w.calories || 0));

// Clickable metric component for history detail row
function HistoryMetric({ metricId, workout, position, onChangeMetric, formatters }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  
  const metric = DETAIL_METRICS.find(m => m.id === metricId) || DETAIL_METRICS[0];
  
  // Get value based on metric id
  const getValue = () => {
    switch (metricId) {
      case 'stroke_rate': return workout.avg_stroke_rate || '—';
      case 'avg_watts': return workout.avg_watts || '—';
      case 'total_strokes': return workout.total_strokes ?? '—';
      case 'max_hr': return workout.max_hr_bpm || '—';
      case 'avg_split': return formatters.formatSplit(workout.avg_split);
      case 'distance': return formatters.formatDistanceMeters(workout.distance);
      case 'calories': return caloriesText(workout);
      case 'duration': return formatters.formatDurationFull(workout.duration);
      case 'avg_hr': return workout.avg_heart_rate || '—';
      default: return 0;
    }
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const value = getValue();
  const displayValue = typeof value === 'number' ? value : value;
  const unitDisplay = ['avg_split', 'distance', 'duration'].includes(metricId) ? '' : metric.unit;
  
  return (
    <div className="relative" ref={dropdownRef}>
      <div 
        onClick={() => setShowDropdown(!showDropdown)}
        className="bg-white rounded-lg p-3 text-center border border-zinc-200 cursor-pointer hover:border-zinc-400 transition-colors"
      >
        <div className="text-xs font-bold uppercase tracking-wider text-zinc-400">{metric.abbr}</div>
        <div className="text-xl font-bold text-zinc-700" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {displayValue}{unitDisplay && <span className="text-sm ml-1">{unitDisplay}</span>}
        </div>
      </div>
      
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-zinc-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
          {DETAIL_METRICS.map((m) => (
            <button
              key={m.id}
              onClick={() => {
                onChangeMetric(position, m.id);
                setShowDropdown(false);
              }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-zinc-100 transition-colors ${
                m.id === metricId ? 'bg-zinc-100 font-bold' : ''
              }`}
            >
              <span className="font-bold text-zinc-500 mr-2">{m.abbr}</span>
              {m.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Session-type badges, filter windows, and the pure workout helpers
// (getWorkoutTitle, isProgramWorkout, getHRZones, formatters,
// windowStart, resolveSessionType) live in `/app/frontend/src/lib/history.js`.
// They are imported at the top of the file.

export default function WorkoutHistory() {
  const navigate = useNavigate();
  const [workouts, setWorkouts] = useState([]);
  const [profileMaxHr, setProfileMaxHr] = useState(null);

  const [loading, setLoading] = useState(true);
  const [expandedWorkout, setExpandedWorkout] = useState(null);
  const [selectedWorkouts, setSelectedWorkouts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Strava connection state (checked from backend, managed in Settings)
  const [stravaConnected, setStravaConnected] = useState(false);
  const [stravaAthlete, setStravaAthlete] = useState(null);

  // Intervals.icu connection state (API key managed in Settings)
  const [intervalsConnected, setIntervalsConnected] = useState(false);
  
  // Batch export state
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [batchExporting, setBatchExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ current: 0, total: 0, platform: '' });
  const exportMenuRef = useRef(null);
  
  // Device History Stats (persists even after deletion)
  const [deviceStats, setDeviceStats] = useState(null);
  const [statsTab, setStatsTab] = useState('week'); // 'week', 'month', 'year'
  // Hard-delete-all confirmation
  const [showClearAllConfirm, setShowClearAllConfirm] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  
  // Filter chip — 'all' | 'week' | 'month' | 'year' — CrewZ v1.6 §7.
  const [filterWindow, setFilterWindow] = useState('all');

  // Persistent load-error state — surfaced to the user instead of silently
  // rendering an empty History. If `/api/workouts` 401s mid-session (cookie
  // expired between ProtectedRoute and this component's mount) we now bounce
  // to `/` for a fresh sign-in rather than showing a blank list.
  const [loadError, setLoadError] = useState(null);
  // Bump this to force the load useEffect to re-run when the user taps Retry.
  const [reloadCounter, setReloadCounter] = useState(0);

  // Selected metrics for detail row (loaded from backend or localStorage)
  const [detailMetrics, setDetailMetrics] = useState(['stroke_rate', 'avg_watts', 'total_strokes']);
  
  // Check for Strava callback params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stravaStatus = params.get('strava');
    
    if (stravaStatus === 'connected') {
      toast.success('Successfully connected to Strava!');
      // Clean up URL
      window.history.replaceState({}, '', '/history');
    } else if (stravaStatus === 'error') {
      const message = params.get('message') || 'Failed to connect to Strava';
      toast.error(message);
      window.history.replaceState({}, '', '/history');
    }
  }, []);
  
  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Load workouts, preferences, and Strava status
  useEffect(() => {
    const loadData = async () => {
      try {
        // Flush any queued offline workouts BEFORE fetching the list — otherwise
        // a session saved-locally-then-not-yet-synced wouldn't appear on this
        // page. The user reported a "session saved" toast but nothing in
        // history, which is exactly this stale-outbox path.
        try {
          const { syncQueue, getOutboxSize } = await import('@/services/offlineSync');
          if (getOutboxSize() > 0) {
            const syncRes = await syncQueue(async (p) => fetch(`${API}/workouts`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify(p),
            }));
            if (syncRes.synced > 0) {
              toast.success(`Synced ${syncRes.synced} pending workout${syncRes.synced === 1 ? '' : 's'}`);
            }
          }
        } catch (e) {
          console.warn('outbox flush failed:', e?.message || e);
        }

        // Load workouts. Prior to iter-64 this block swallowed non-OK
        // responses silently (any 401 or 500 → `workouts` stayed `[]` and
        // the page rendered the empty state as if the account had no
        // history). That looked identical to a real data-loss bug — see
        // the iter-63 field report where the rower's sessions
        // "disappeared" until they re-authenticated. Fix: differentiate
        // between auth expiry (bounce to `/` for fresh sign-in) and other
        // failures (surface an inline error with a Retry button).
        const workoutsRes = await fetch(`${API}/workouts`, { credentials: 'include' });
        if (workoutsRes.status === 401) {
          // Session expired between ProtectedRoute and here. Force re-auth.
          console.warn('[History] /api/workouts returned 401 — session expired, redirecting to /');
          toast.error('Your session has expired. Please sign in again.', { duration: 4000 });
          setLoadError({ kind: 'auth', message: 'Session expired.' });
          setTimeout(() => { window.location.href = '/'; }, 300);
          return;
        }
        if (!workoutsRes.ok) {
          throw new Error(`Failed to load workouts (HTTP ${workoutsRes.status})`);
        }
        const data = await workoutsRes.json();
        setWorkouts(data);

        fetch(`${API}/user/profile`, { credentials: 'include' })
          .then((r) => (r.ok ? r.json() : null))
          .then((u) => { if (u) setProfileMaxHr(maxHrInUse(u)); })
          .catch(() => {});
        
        // Load metric preferences from backend
        const prefsRes = await fetch(`${API}/users/preferences`, { credentials: 'include' });
        if (prefsRes.ok) {
          const prefs = await prefsRes.json();
          if (prefs.history_metrics && prefs.history_metrics.length === 3) {
            setDetailMetrics(prefs.history_metrics);
            localStorage.setItem('history_detail_metrics', JSON.stringify(prefs.history_metrics));
          }
        }
        
        // Load Strava status
        const stravaRes = await fetch(`${API}/strava/status`, { credentials: 'include' });
        if (stravaRes.ok) {
          const stravaData = await stravaRes.json();
          setStravaConnected(stravaData.connected);
          if (stravaData.connected) {
            setStravaAthlete(stravaData.athlete_name);
          }
        }

        // Load Intervals.icu connection status
        const intervalsRes = await fetch(`${API}/intervals/status`, { credentials: 'include' });
        if (intervalsRes.ok) {
          const intervalsData = await intervalsRes.json();
          setIntervalsConnected(!!intervalsData.connected);
        }
        
        // Load Device History Stats (persists even after deletion)
        const statsRes = await fetch(`${API}/device-history-stats`, { credentials: 'include' });
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setDeviceStats(statsData);
        }
      } catch (error) {
        console.error('Failed to load data:', error);
        // Surface the failure — the previous silent-swallow made real data
        // loss indistinguishable from a legitimate empty history.
        setLoadError({
          kind: 'network',
          message: error?.message || 'Could not load your history. Check your connection and try again.',
        });
        // Fallback to localStorage for metrics (best-effort, not fatal).
        const saved = localStorage.getItem('history_detail_metrics');
        if (saved) setDetailMetrics(JSON.parse(saved));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [reloadCounter]);
  
  // Save metric preferences to backend
  const handleChangeMetric = async (position, metricId) => {
    const newMetrics = [...detailMetrics];
    newMetrics[position] = metricId;
    setDetailMetrics(newMetrics);
    localStorage.setItem('history_detail_metrics', JSON.stringify(newMetrics));
    
    // Save to backend
    try {
      await fetch(`${API}/users/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ history_metrics: newMetrics })
      });
    } catch (error) {
      console.log('Failed to save metric preferences to backend');
    }
  };

  // Calculate summary stats — windowed by the current filter chip.
  // Legacy rows use `duration`; v1.6 rows prefer `moving_time_s` per spec §7.
  const filteredWorkouts = (() => {
    const start = windowStart(filterWindow);
    if (!start) return workouts;
    return workouts.filter((w) => {
      const t = w?.start_time ? new Date(w.start_time) : null;
      return t && !Number.isNaN(t.getTime()) && t >= start;
    });
  })();
  const totalWorkouts = filteredWorkouts.length;
  const totalDistance = filteredWorkouts.reduce((sum, w) => sum + (w.distance || 0), 0);
  const totalTime = filteredWorkouts.reduce((sum, w) => sum + (w.moving_time_s ?? w.duration ?? 0), 0);
  const totalCalories = Math.floor(filteredWorkouts.reduce((sum, w) => sum + (w.calories || 0), 0));

  // Formatters, HR zone bucketer, program/title helpers now live in
  // `/app/frontend/src/lib/history.js`.

  const toggleExpand = (workoutId) => {
    setExpandedWorkout(expandedWorkout === workoutId ? null : workoutId);
  };

  const toggleSelectWorkout = (workoutId) => {
    setSelectedWorkouts(prev => 
      prev.includes(workoutId) 
        ? prev.filter(id => id !== workoutId)
        : [...prev, workoutId]
    );
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedWorkouts([]);
    } else {
      setSelectedWorkouts(workouts.map(w => w.workout_id));
    }
    setSelectAll(!selectAll);
  };

  // Delete selected workouts
  const deleteSelectedWorkouts = async () => {
    if (selectedWorkouts.length === 0) {
      toast.error('No workouts selected');
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedWorkouts.length} workout${selectedWorkouts.length > 1 ? 's' : ''}? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      toast.loading('Deleting workouts...', { id: 'delete-workouts' });

      const response = await fetch(`${API}/workouts/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ workout_ids: selectedWorkouts })
      });

      if (response.ok) {
        const result = await response.json();
        setWorkouts(prev => prev.filter(w => !selectedWorkouts.includes(w.workout_id)));
        setSelectedWorkouts([]);
        setSelectAll(false);
        toast.success(`${result.deleted_count || selectedWorkouts.length} workout${selectedWorkouts.length > 1 ? 's' : ''} deleted`, { id: 'delete-workouts' });
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete workouts', { id: 'delete-workouts' });
    }
  };

  // Toggle a workout's benchmark status. Only one benchmark per boat_type
  // per user — the backend automatically demotes any existing benchmark in
  // the same boat class when a new one is promoted.
  const toggleBenchmark = async (workout) => {
    const willBecome = !workout.is_benchmark;
    try {
      const url = `${API}/workouts/${workout.workout_id}/benchmark`;
      const response = await fetch(url, {
        method: willBecome ? 'POST' : 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      // Optimistic: also clear other benchmarks in the same boat_type locally
      setWorkouts((prev) =>
        prev.map((w) => {
          if (w.workout_id === workout.workout_id) return { ...w, is_benchmark: willBecome };
          if (willBecome && w.boat_type === workout.boat_type) return { ...w, is_benchmark: false };
          return w;
        })
      );
      toast.success(
        willBecome
          ? `Set as benchmark for ${workout.boat_type || 'this boat'}`
          : 'Benchmark removed'
      );
    } catch (e) {
      console.error('Benchmark toggle error:', e);
      toast.error('Could not update benchmark');
    }
  };

  // Hard-delete every workout AND reset device_history_stats. Used to recover
  // from corrupted/stacked-session data (the 1217h impossible totals bug).
  const clearAllHistory = async () => {
    setClearingAll(true);
    try {
      toast.loading('Clearing all history…', { id: 'clear-all' });
      const response = await fetch(`${API}/workouts/all`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const result = await response.json();
      setWorkouts([]);
      setSelectedWorkouts([]);
      setSelectAll(false);
      setDeviceStats({
        week: { duration: 0, distance: 0, sessions: 0 },
        month: { duration: 0, distance: 0, sessions: 0 },
        year: { duration: 0, distance: 0, sessions: 0 },
        total: { duration: 0, distance: 0, sessions: 0 },
      });
      setShowClearAllConfirm(false);
      toast.success(
        `Cleared ${result.deleted_workouts || 0} workout(s) and reset history totals.`,
        { id: 'clear-all' }
      );
    } catch (error) {
      console.error('Clear-all error:', error);
      toast.error('Failed to clear history', { id: 'clear-all' });
    } finally {
      setClearingAll(false);
    }
  };

  // Export to a platform (Strava, Intervals.icu, etc.) via API or download TCX
  const exportWorkout = async (workout, platform = 'fit') => {
    // If 'fit' (legacy id, now produces TCX), download the file locally
    if (platform === 'fit') {
      try {
        const xml = generateTcxFile(tcxPayloadFromWorkout(workout));
        // application/octet-stream forces "Save As" in every browser & keeps
        // the `.tcx` extension. The `application/vnd.garmin.tcx+xml` MIME we
        // were using before was making Chrome try to render it inline.
        const blob = new Blob([xml], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const filename = `crewz-workout-${workout.workout_id || new Date(workout.start_time).toISOString().split('T')[0]}.tcx`;
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.rel = 'noopener';
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        // Console breadcrumb so the user can verify in DevTools what fired.
        // eslint-disable-next-line no-console
        console.info(`[CrewZ] Triggered download: ${filename} (${blob.size} bytes)`);

        // Backup link in the toast — some browsers / Windows SmartScreen
        // silently swallow programmatic downloads. Clicking this link is a
        // user-initiated gesture so it always works.
        toast.success(
          <div className="flex flex-col gap-1">
            <div>Downloaded <strong>{filename}</strong></div>
            <a
              href={url}
              download={filename}
              className="text-blue-500 hover:underline text-xs"
              onClick={() => setTimeout(() => URL.revokeObjectURL(url), 30000)}
            >
              Not in Downloads? Click here to retry →
            </a>
          </div>,
          { duration: 8000 }
        );
        // Revoke after a longer window so the manual retry link still works.
        setTimeout(() => URL.revokeObjectURL(url), 30000);
      } catch (error) {
        toast.error(`Failed to export TCX file: ${error?.message || error}`);
        console.error('TCX export error:', error);
      }
      return;
    }

    // For STRAVA - real API integration (Strava accepts TCX natively)
    if (platform.toLowerCase() === 'strava') {
      if (!stravaConnected) {
        toast.error('Please connect your Strava account first');
        return;
      }

      try {
        toast.loading('Uploading to Strava...', { id: `export-${workout.workout_id}` });

        const xml = generateTcxFile(tcxPayloadFromWorkout(workout));
        // Backend expects base64-encoded bytes under `fit_data` (legacy key
        // name) — the field accepts any supported activity format, and the
        // backend just forwards it on to Strava.
        const base64Data = btoa(unescape(encodeURIComponent(xml)));

        const response = await fetch(`${API}/strava/upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            workout_id: workout.workout_id,
            fit_data: base64Data,
            data_type: 'tcx',
          })
        });

        const result = await response.json();

        if (response.ok && result.success) {
          toast.success(result.message, { id: `export-${workout.workout_id}` });
        } else {
          throw new Error(result.detail || 'Upload failed');
        }
      } catch (error) {
        console.error('Strava upload error:', error);
        toast.error(`Strava upload failed: ${error.message}`, { id: `export-${workout.workout_id}` });
      }
      return;
    }

    // INTERVALS.ICU — real API upload of the TCX file.
    if (platform.toLowerCase() === 'intervals' || platform.toLowerCase() === 'intervalsicu') {
      if (!intervalsConnected) {
        toast.error('Please connect your Intervals.icu account in Settings first');
        return;
      }
      try {
        toast.loading('Uploading to Intervals.icu...', { id: `export-${workout.workout_id}` });

        const xml = generateTcxFile(tcxPayloadFromWorkout(workout));
        const base64Data = btoa(unescape(encodeURIComponent(xml)));

        const response = await fetch(`${API}/intervals/upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            workout_id: workout.workout_id,
            fit_data: base64Data,
            data_type: 'tcx',
            name: workout.session_name || 'CREWZ Workout',
            description: `Exported from CREWZ${workout.boat_type ? ` · ${workout.boat_type}` : ''}`,
          })
        });

        const result = await response.json();

        if (response.ok && result.success) {
          toast.success(
            <div>
              <strong>{result.message}</strong>
              {result.activity_url && (
                <div className="mt-1">
                  <a
                    href={result.activity_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline text-sm"
                  >
                    View on Intervals.icu →
                  </a>
                </div>
              )}
            </div>,
            { id: `export-${workout.workout_id}`, duration: 10000 }
          );
        } else {
          throw new Error(result.detail || 'Upload failed');
        }
      } catch (error) {
        console.error('Intervals.icu upload error:', error);
        toast.error(`Intervals.icu upload failed: ${error.message}`, { id: `export-${workout.workout_id}` });
      }
      return;
    }

    // For OTHER platform exports (Garmin, etc.), use mock API for now
    const platformKey = platform.toLowerCase().replace(' ', '');
    const platformMap = {
      'garmin': 'garmin',
      'concept2': 'concept2',
      'trainingpeaks': 'trainingpeaks',
      'googlefit': 'googlefit',
      'rowsandall': 'rowsandall'
    };

    const apiPlatform = platformMap[platformKey] || platformKey;
    
    try {
      toast.loading(`Uploading to ${platform}...`, { id: `export-${workout.workout_id}` });
      
      const response = await fetch(`${API}/workouts/${workout.workout_id}/export/${apiPlatform}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(
          <div>
            <strong>{result.message}</strong>
            {result.export_url && (
              <div className="mt-1">
                <a 
                  href={result.export_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:underline text-sm"
                >
                  View on {result.platform} →
                </a>
              </div>
            )}
          </div>,
          { id: `export-${workout.workout_id}`, duration: 5000 }
        );
      } else {
        throw new Error(result.message || 'Export failed');
      }
    } catch (error) {
      toast.error(`Failed to export to ${platform}`, { id: `export-${workout.workout_id}` });
      console.error('Export error:', error);
    }
  };

  // Batch export to a specific platform
  const batchExportToPlatform = async (platformId) => {
    if (selectedWorkouts.length === 0) {
      toast.error('Please select workouts to export');
      return;
    }
    
    setShowExportMenu(false);
    
    // For FIT/TCX files, download all immediately
    if (platformId === 'fit') {
      selectedWorkouts.forEach(id => {
        const workout = workouts.find(w => w.workout_id === id);
        if (workout) exportWorkout(workout, 'fit');
      });
      toast.success(`Downloaded ${selectedWorkouts.length} TCX file${selectedWorkouts.length > 1 ? 's' : ''}`);
      return;
    }

    // Check Strava connection for Strava exports - prompt to connect in Settings
    if (platformId === 'strava' && !stravaConnected) {
      toast(
        <div className="flex flex-col gap-2">
          <div className="font-bold">Connect Strava First</div>
          <div className="text-sm text-zinc-500">Connect your Strava account in Settings to upload workouts.</div>
          <button
            onClick={() => { navigate('/settings'); toast.dismiss(); }}
            className="mt-1 px-3 py-1.5 bg-orange-500 text-white rounded-lg text-sm font-bold hover:bg-orange-600 transition-colors"
          >
            Go to Settings
          </button>
        </div>,
        { duration: 5000 }
      );
      return;
    }

    // Same gate for Intervals.icu — direct the user to Settings if no API key.
    if (platformId === 'intervals' && !intervalsConnected) {
      toast(
        <div className="flex flex-col gap-2">
          <div className="font-bold">Connect Intervals.icu First</div>
          <div className="text-sm text-zinc-500">Add your Intervals.icu API key in Settings to upload workouts.</div>
          <button
            onClick={() => { navigate('/settings'); toast.dismiss(); }}
            className="mt-1 px-3 py-1.5 bg-purple-500 text-white rounded-lg text-sm font-bold hover:bg-purple-600 transition-colors"
          >
            Go to Settings
          </button>
        </div>,
        { duration: 5000 }
      );
      return;
    }
    
    const platform = EXPORT_PLATFORMS.find(p => p.id === platformId);
    const platformName = platform?.name || platformId;
    
    setBatchExporting(true);
    setExportProgress({ current: 0, total: selectedWorkouts.length, platform: platformName });
    
    let successCount = 0;
    let failCount = 0;
    
    const toastId = `batch-export-${platformId}`;
    toast.loading(`Uploading to ${platformName}... (0/${selectedWorkouts.length})`, { id: toastId });
    
    for (let i = 0; i < selectedWorkouts.length; i++) {
      const workoutId = selectedWorkouts[i];
      const workout = workouts.find(w => w.workout_id === workoutId);
      
      if (!workout) continue;
      
      setExportProgress({ current: i + 1, total: selectedWorkouts.length, platform: platformName });
      toast.loading(`Uploading to ${platformName}... (${i + 1}/${selectedWorkouts.length})`, { id: toastId });
      
      try {
        if (platformId === 'strava' || platformId === 'intervals') {
          // Real upload — generate TCX, base64-encode, POST
          const xml = generateTcxFile(tcxPayloadFromWorkout(workout));
          const base64Data = btoa(unescape(encodeURIComponent(xml)));

          const endpoint = platformId === 'strava' ? `${API}/strava/upload` : `${API}/intervals/upload`;
          const body = {
            workout_id: workout.workout_id,
            fit_data: base64Data,
            data_type: 'tcx',
          };
          if (platformId === 'intervals') {
            body.name = workout.session_name || 'CREWZ Workout';
            body.description = `Exported from CREWZ${workout.boat_type ? ` · ${workout.boat_type}` : ''}`;
          }

          const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(body),
          });

          const result = await response.json();
          if (response.ok && result.success) {
            successCount++;
          } else {
            failCount++;
          }
        } else {
          // Mock upload for other platforms
          const response = await fetch(`${API}/workouts/${workout.workout_id}/export/${platformId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include'
          });
          
          const result = await response.json();
          if (response.ok && result.success) {
            successCount++;
          } else {
            failCount++;
          }
        }
        
        // Small delay between uploads to avoid rate limiting
        if (i < selectedWorkouts.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      } catch (error) {
        console.error(`Failed to export workout ${workoutId}:`, error);
        failCount++;
      }
    }
    
    setBatchExporting(false);
    setExportProgress({ current: 0, total: 0, platform: '' });
    
    if (failCount === 0) {
      toast.success(`Successfully uploaded ${successCount} workout${successCount > 1 ? 's' : ''} to ${platformName}!`, { id: toastId });
    } else if (successCount === 0) {
      toast.error(`Failed to upload workouts to ${platformName}`, { id: toastId });
    } else {
      toast.warning(`Uploaded ${successCount} workout${successCount > 1 ? 's' : ''}, ${failCount} failed`, { id: toastId });
    }
  };

  // Legacy bulk export (downloads FIT files)
  const bulkExport = () => {
    if (selectedWorkouts.length === 0) {
      setShowExportMenu(!showExportMenu);
      return;
    }
    setShowExportMenu(!showExportMenu);
  };

  // isProgramWorkout / getWorkoutTitle live in `/app/frontend/src/lib/history.js`.

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-900 flex flex-col transition-colors">
      <TopNav />
      <div className="flex-1 py-6 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-6 flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-extrabold tracking-tighter text-zinc-900 dark:text-white" style={{ fontFamily: 'Archivo, sans-serif' }}>
                Training Session Analysis
              </h1>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                Review your rowing performance and export workouts to fitness platforms.
              </p>
            </div>
            <div className="flex gap-2">
              <button 
                className="px-4 py-2 border-2 border-zinc-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 text-sm font-bold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors flex items-center gap-2"
                data-testid="filters-btn"
              >
                <Filter className="w-4 h-4" />
                Filters
              </button>
              <div className="relative" ref={exportMenuRef}>
                <button 
                  onClick={bulkExport}
                  disabled={batchExporting}
                  className="px-4 py-2 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-lg text-sm font-bold hover:bg-zinc-800 dark:hover:bg-zinc-100 transition-colors flex items-center gap-2 disabled:opacity-50"
                  data-testid="bulk-export-btn"
                >
                  {batchExporting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white dark:border-zinc-900 border-t-transparent rounded-full animate-spin"></div>
                      {exportProgress.current}/{exportProgress.total}
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Bulk Export
                      <ChevronDown className="w-3 h-3" />
                    </>
                  )}
                </button>
                
                {/* Export Platform Dropdown */}
                {showExportMenu && (
                  <div className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-xl shadow-lg z-50 overflow-hidden">
                    <div className="p-2 border-b border-zinc-100 dark:border-zinc-700">
                      <p className="text-xs font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider px-2">
                        {selectedWorkouts.length > 0 
                          ? `Export ${selectedWorkouts.length} workout${selectedWorkouts.length > 1 ? 's' : ''} to:`
                          : 'Select workouts first'}
                      </p>
                    </div>
                    <div className="py-1">
                      {EXPORT_PLATFORMS.map((platform) => {
                        const isDisabled = selectedWorkouts.length === 0 ||
                          (platform.id === 'strava' && !stravaConnected) ||
                          (platform.id === 'intervals' && !intervalsConnected);
                        const IconComponent = platform.icon;
                        
                        return (
                          <button
                            key={platform.id}
                            onClick={() => batchExportToPlatform(platform.id)}
                            disabled={isDisabled}
                            className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors text-left ${
                              isDisabled ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            data-testid={`bulk-export-${platform.id}`}
                          >
                            <div className={`w-8 h-8 ${platform.bgColor} dark:bg-opacity-20 rounded-lg flex items-center justify-center`}>
                              {IconComponent ? (
                                <IconComponent className={`w-4 h-4 ${platform.color}`} />
                              ) : platform.id === 'strava' ? (
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#FC4C02">
                                  <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.599h4.172L10.463 0l-7 13.828h4.169"/>
                                </svg>
                              ) : platform.id === 'garmin' ? (
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                                  <circle cx="12" cy="12" r="10" stroke="#007CC3" strokeWidth="2"/>
                                  <circle cx="12" cy="12" r="5" stroke="#007CC3" strokeWidth="2"/>
                                </svg>
                              ) : platform.id === 'intervals' ? (
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                                  <rect x="4" y="8" width="3" height="10" rx="1" fill="#8B5CF6"/>
                                  <rect x="10" y="4" width="3" height="14" rx="1" fill="#A78BFA"/>
                                  <rect x="16" y="6" width="3" height="12" rx="1" fill="#C4B5FD"/>
                                </svg>
                              ) : platform.id === 'trainingpeaks' ? (
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                                  <path d="M12 4L4 10l8 6 8-6-8-6z" fill="#E31937"/>
                                  <path d="M4 14l8 6 8-6" stroke="#E31937" strokeWidth="2"/>
                                </svg>
                              ) : platform.id === 'googlefit' ? (
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                                  <path d="M12 4v6M12 14v6M6 10v4M18 10v4" stroke="#4285F4" strokeWidth="3" strokeLinecap="round"/>
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                                  <path d="M4 12h16M4 12l4-4m-4 4l4 4" stroke="#0EA5E9" strokeWidth="2.5" strokeLinecap="round"/>
                                  <circle cx="20" cy="12" r="2" fill="#0EA5E9"/>
                                </svg>
                              )}
                            </div>
                            <div className="flex-1">
                              <div className={`font-bold text-sm ${platform.color} dark:text-zinc-200`}>
                                {platform.name}
                              </div>
                              {platform.requiresConnection && platform.id === 'strava' && !stravaConnected && (
                                <div className="text-xs text-zinc-400">Connect account first</div>
                              )}
                              {platform.requiresConnection && platform.id === 'intervals' && !intervalsConnected && (
                                <div className="text-xs text-zinc-400">Add API key first</div>
                              )}
                              {platform.mocked && (
                                <div className="text-xs text-zinc-400">Simulated upload</div>
                              )}
                            </div>
                            {platform.id === 'strava' && stravaConnected && (
                              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            )}
                            {platform.id === 'intervals' && intervalsConnected && (
                              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Device History Stats - Persists even after deletion */}
          {!loading && deviceStats && (
            <div className="bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-xl mb-6 overflow-hidden" data-testid="device-history-stats">
              <div className="flex border-b border-zinc-200 dark:border-zinc-700">
                {['week', 'month', 'year'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setStatsTab(tab)}
                    className={`flex-1 py-3 px-4 text-sm font-bold uppercase tracking-wider transition-colors ${
                      statsTab === tab
                        ? 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900'
                        : 'bg-zinc-50 dark:bg-zinc-700 text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-600'
                    }`}
                    data-testid={`stats-tab-${tab}`}
                  >
                    {tab === 'week' ? 'This Week' : tab === 'month' ? 'This Month' : 'This Year'}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-0">
                <div className="p-5 text-center border-r border-zinc-100 dark:border-zinc-700">
                  <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">Time</div>
                  <div className="text-2xl font-bold text-zinc-800 dark:text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {formatDuration(deviceStats[statsTab]?.duration || 0)}
                  </div>
                </div>
                <div className="p-5 text-center border-r border-zinc-100 dark:border-zinc-700">
                  <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">Sessions</div>
                  <div className="text-2xl font-bold text-zinc-800 dark:text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {deviceStats[statsTab]?.sessions || 0}
                  </div>
                </div>
                <div className="p-5 text-center">
                  <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">Distance</div>
                  <div className="text-2xl font-bold text-zinc-800 dark:text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {formatDistance(deviceStats[statsTab]?.distance || 0)}
                  </div>
                </div>
              </div>
              <div className="bg-zinc-50 dark:bg-zinc-700/50 px-4 py-2 flex items-center justify-between gap-3">
                <span className="text-xs text-zinc-400">
                  Device lifetime totals — persist even when workouts are deleted
                </span>
                <button
                  type="button"
                  onClick={() => setShowClearAllConfirm(true)}
                  className="text-xs font-bold uppercase tracking-wider text-rose-600 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300 transition-colors flex items-center gap-1"
                  data-testid="clear-all-history-btn"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Clear all history
                </button>
              </div>
            </div>
          )}

          {/* Clear All History confirmation modal */}
          {showClearAllConfirm && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
              data-testid="clear-all-modal"
            >
              <div className="bg-white dark:bg-zinc-800 rounded-2xl shadow-2xl max-w-md w-full p-6 border-2 border-rose-200 dark:border-rose-900">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 bg-rose-100 dark:bg-rose-900/40 rounded-full flex items-center justify-center flex-shrink-0">
                    <Trash2 className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-zinc-900 dark:text-white mb-1">
                      Clear all workout history?
                    </h3>
                    <p className="text-sm text-zinc-600 dark:text-zinc-300">
                      This hard-deletes <strong>every workout</strong> on your account and resets the Week / Month / Year / Total counters to zero. Use this to recover from corrupted session data. <strong>This cannot be undone.</strong>
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowClearAllConfirm(false)}
                    disabled={clearingAll}
                    className="px-4 py-2 rounded-lg text-sm font-bold text-zinc-700 dark:text-zinc-200 bg-zinc-100 dark:bg-zinc-700 hover:bg-zinc-200 dark:hover:bg-zinc-600 transition-colors disabled:opacity-50"
                    data-testid="clear-all-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={clearAllHistory}
                    disabled={clearingAll}
                    className="px-4 py-2 rounded-lg text-sm font-bold text-white bg-rose-600 hover:bg-rose-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    data-testid="clear-all-confirm-btn"
                  >
                    {clearingAll && (
                      <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    )}
                    Delete everything
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Selection Actions Bar - Shown when workouts are selected */}
          {selectedWorkouts.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/30 border-2 border-blue-200 dark:border-blue-800 rounded-xl p-4 mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={toggleSelectAll}
                  className="p-2 hover:bg-blue-100 dark:hover:bg-blue-800 rounded-lg transition-colors"
                  data-testid="select-all-btn"
                >
                  {selectAll ? (
                    <CheckSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  ) : (
                    <Square className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  )}
                </button>
                <span className="font-bold text-blue-700 dark:text-blue-300">
                  {selectedWorkouts.length} workout{selectedWorkouts.length > 1 ? 's' : ''} selected
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setSelectedWorkouts([]); setSelectAll(false); }}
                  className="px-3 py-2 text-sm font-bold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                {/* Quick export buttons */}
                <button
                  onClick={() => batchExportToPlatform('fit')}
                  className="px-3 py-2 border-2 border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 rounded-lg text-sm font-bold hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors flex items-center gap-2"
                  data-testid="quick-export-fit"
                >
                  <Download className="w-4 h-4" />
                  TCX
                </button>
                <button
                  onClick={() => batchExportToPlatform('strava')}
                  disabled={batchExporting}
                  className={`px-3 py-2 rounded-lg text-sm font-bold transition-colors flex items-center gap-2 disabled:opacity-50 ${
                    stravaConnected 
                      ? 'bg-orange-500 text-white hover:bg-orange-600' 
                      : 'border-2 border-orange-300 dark:border-orange-700 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/30'
                  }`}
                  data-testid="quick-export-strava"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.599h4.172L10.463 0l-7 13.828h4.169"/>
                  </svg>
                  Strava
                </button>
                <button
                  onClick={deleteSelectedWorkouts}
                  data-testid="delete-selected-btn"
                  className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-bold hover:bg-red-600 transition-colors flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          )}

          {/* Load-error banner — surfaces auth/network failures instead of
              rendering the empty-state as if the user has no workouts. See
              iter-64 field report where sessions "disappeared" until the
              rower re-authenticated. `kind: 'auth'` short-circuits to a
              redirect in the load effect; anything else lands here. */}
          {loadError && loadError.kind !== 'auth' && (
            <div
              className="mb-6 rounded-xl border-2 border-rose-200 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-950/30 p-4 flex items-start gap-3"
              data-testid="history-load-error"
              role="alert"
            >
              <div className="w-8 h-8 rounded-full bg-rose-100 dark:bg-rose-900/60 flex items-center justify-center flex-shrink-0">
                <span className="text-rose-600 dark:text-rose-300 font-bold">!</span>
              </div>
              <div className="flex-1">
                <div className="font-bold text-rose-800 dark:text-rose-200 text-sm">
                  Couldn't load your history
                </div>
                <div className="text-xs text-rose-700 dark:text-rose-300 mt-0.5">
                  {loadError.message}
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    type="button"
                    onClick={() => {
                      setLoadError(null);
                      setLoading(true);
                      setReloadCounter((n) => n + 1);
                    }}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-rose-600 text-white hover:bg-rose-700 transition-colors"
                    data-testid="history-load-retry-btn"
                  >
                    Retry
                  </button>
                  <button
                    type="button"
                    onClick={() => { window.location.href = '/'; }}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-white dark:bg-zinc-800 border-2 border-rose-200 dark:border-rose-900/40 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/50 transition-colors"
                    data-testid="history-signin-again-btn"
                  >
                    Sign in again
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Filter chips — CrewZ Session Data Model v1.6 §7 */}
          {!loading && !loadError && workouts.length > 0 && (
            <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="history-filter-chips">
              {FILTER_WINDOWS.map((w) => {
                const active = filterWindow === w.id;
                return (
                  <button
                    type="button"
                    key={w.id}
                    onClick={() => setFilterWindow(w.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                      active
                        ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900'
                        : 'bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:border-zinc-400'
                    }`}
                    data-testid={`filter-chip-${w.id}`}
                  >
                    {w.label}
                  </button>
                );
              })}
              {filterWindow !== 'all' && (
                <span className="text-xs text-zinc-400 ml-1" data-testid="filter-window-count">
                  {filteredWorkouts.length} of {workouts.length} sessions
                </span>
              )}
            </div>
          )}

          {/* Summary Cards */}
          {!loading && workouts.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-xl p-4" data-testid="total-workouts-card">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-zinc-400">Total Workouts</div>
                    <div className="text-xl font-bold text-zinc-800 dark:text-white">{totalWorkouts} {totalWorkouts === 1 ? 'session' : 'sessions'}</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-xl p-4" data-testid="total-distance-card">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                    <Link2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                      Total Distance
                      <span className="px-1.5 py-0.5 bg-green-100 text-green-600 text-xs font-bold rounded">PR</span>
                    </div>
                    <div className="text-xl font-bold text-zinc-800">{(totalDistance / 1000).toFixed(1)} km</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white border-2 border-zinc-200 rounded-xl p-4" data-testid="total-time-card">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-zinc-400">Total Time</div>
                    <div className="text-xl font-bold text-zinc-800">{formatDuration(totalTime)}</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white border-2 border-zinc-200 rounded-xl p-4" data-testid="total-calories-card">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <Flame className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-zinc-400">Total Calories</div>
                    <div className="text-xl font-bold text-zinc-800">{totalCalories.toLocaleString()} kcal</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Bulk Select — hidden when the current filter window is empty
              so an empty state doesn't render a checkbox row against nothing. */}
          {!loading && workouts.length > 0 && filteredWorkouts.length > 0 && (
            <div className="flex items-center gap-3 mb-4 px-2">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={toggleSelectAll}
                className="w-4 h-4 rounded border-zinc-300"
                data-testid="select-all-checkbox"
              />
              <span className="text-sm text-zinc-600">Select workouts for bulk export.</span>
              <span className="text-sm text-zinc-400">{totalWorkouts} total</span>
            </div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-zinc-300 border-t-zinc-900 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-sm text-zinc-500">Loading workouts...</p>
            </div>
          ) : workouts.length === 0 ? (
            <div className="text-center py-16 bg-white border-2 border-zinc-200 rounded-xl" data-testid="no-workouts-message">
              <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-zinc-400" />
              </div>
              <p className="text-lg font-semibold text-zinc-700">No workouts yet</p>
              <p className="text-sm text-zinc-500 mt-2">Start your first workout to see it here</p>
              <button
                onClick={() => navigate('/dashboard')}
                className="mt-6 px-6 py-3 bg-zinc-900 text-white font-bold rounded-lg hover:bg-zinc-800 transition-colors"
              >
                Start Workout
              </button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="workout-list">
              {filteredWorkouts.length === 0 && filterWindow !== 'all' && (
                <div className="text-center py-8 bg-white dark:bg-zinc-800 border-2 border-dashed border-zinc-200 dark:border-zinc-700 rounded-xl" data-testid="filter-empty-state">
                  <p className="text-sm text-zinc-500 dark:text-zinc-400">
                    No sessions in this window.
                  </p>
                  <button
                    type="button"
                    onClick={() => setFilterWindow('all')}
                    className="mt-2 text-xs font-bold uppercase tracking-wider text-blue-600 hover:text-blue-700"
                    data-testid="reset-filter-btn"
                  >
                    Show all time
                  </button>
                </div>
              )}
              {filteredWorkouts.map((workout) => {
                const isExpanded = expandedWorkout === workout.workout_id;
                const isSelected = selectedWorkouts.includes(workout.workout_id);
                const hrZones = getHRZones(workout);
                const isProgram = isProgramWorkout(workout);
                
                return (
                  <div
                    key={workout.workout_id}
                    className={`bg-white border-2 rounded-xl shadow-sm overflow-hidden transition-all ${
                      isSelected ? 'border-blue-400' : isProgram ? 'border-green-200' : 'border-zinc-200'
                    }`}
                    data-testid={`workout-${workout.workout_id}`}
                  >
                    {/* Program indicator bar */}
                    {isProgram && (
                      <div className="h-1 bg-gradient-to-r from-green-400 to-green-600" />
                    )}
                    
                    {/* Workout Header Row — click anywhere on the body to expand the analysis */}
                    <div
                      className="p-4 cursor-pointer hover:bg-zinc-50 transition-colors"
                      onClick={() => toggleExpand(workout.workout_id)}
                      data-testid={`workout-row-${workout.workout_id}`}
                    >
                      <div className="flex items-center gap-4">
                        {/* Checkbox */}
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleSelectWorkout(workout.workout_id); }}
                          className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded transition-colors"
                          data-testid={`select-${workout.workout_id}`}
                        >
                          {isSelected ? (
                            <CheckSquare className="w-5 h-5 text-blue-500" />
                          ) : (
                            <Square className="w-5 h-5 text-zinc-400 dark:text-zinc-500" />
                          )}
                        </button>
                        
                        {/* Boat Type Icon - Shows boat class or activity icon */}
                        <div className={`w-12 h-14 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isProgram ? 'bg-green-50 dark:bg-green-900/30' : 'bg-blue-50 dark:bg-blue-900/30'
                        }`}>
                          {workout.boat_type && getBoatById(workout.boat_type) ? (
                            <BoatTypeIcon 
                              boatType={workout.boat_type} 
                              size="md"
                              className={isProgram ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'}
                            />
                          ) : isProgram ? (
                            <ClipboardList className="w-5 h-5 text-green-600 dark:text-green-400" />
                          ) : (
                            <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          )}
                        </div>
                        
                        {/* Title & Date */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3
                              className="text-lg font-bold text-zinc-800 dark:text-white"
                              style={{ fontFamily: 'Archivo, sans-serif' }}
                              data-testid={`workout-title-${workout.workout_id}`}
                            >
                              {getWorkoutTitle(workout)}
                            </h3>
                            {/* Boat Class Badge */}
                            {workout.boat_type && getBoatById(workout.boat_type) && (
                              <span className="px-2 py-0.5 bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 text-xs font-bold rounded-full uppercase tracking-wider">
                                {getBoatById(workout.boat_type).name}
                              </span>
                            )}
                            {/* Session-type badge — v1.6 §3.1. Also
                                serves as the visible "Program" chip for
                                program rows so we don't stack two pills. */}
                            {(() => {
                              const t = resolveSessionType(workout);
                              if (t === 'standard') return null;
                              const b = SESSION_TYPE_BADGES[t];
                              return (
                                <span
                                  className={`px-2 py-0.5 text-xs font-bold rounded-full uppercase tracking-wider ${b.className}`}
                                  data-testid={`session-type-badge-${workout.workout_id}`}
                                >
                                  {b.label}
                                </span>
                              );
                            })()}
                            {/* Benchmark badge — clickable to toggle */}
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); toggleBenchmark(workout); }}
                              className={`px-2 py-0.5 text-xs font-bold rounded-full uppercase tracking-wider flex items-center gap-1 transition-colors ${
                                workout.is_benchmark
                                  ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 hover:bg-amber-200'
                                  : 'bg-zinc-100 dark:bg-zinc-700 text-zinc-500 dark:text-zinc-400 hover:bg-amber-50 hover:text-amber-700'
                              }`}
                              data-testid={`benchmark-toggle-${workout.workout_id}`}
                              title={workout.is_benchmark
                                ? 'This is your benchmark for this boat. Click to unset.'
                                : 'Set as benchmark for this boat — AI will compare future sessions against it.'}
                            >
                              <Star className={`w-3 h-3 ${workout.is_benchmark ? 'fill-current' : ''}`} />
                              {workout.is_benchmark ? 'Benchmark' : 'Set Benchmark'}
                            </button>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-zinc-500 dark:text-zinc-400">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {formatDate(workout.start_time)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {formatDurationFull(workout.duration)}
                            </span>
                          </div>
                        </div>
                        
                        {/* Key Metrics */}
                        <div className="hidden md:flex items-center gap-6">
                          <div className="text-center">
                            <div className="text-xs font-bold uppercase tracking-wider text-zinc-400">Distance</div>
                            <div className="text-lg font-bold text-zinc-700" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                              {formatDistanceMeters(workout.distance)}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-xs font-bold uppercase tracking-wider text-zinc-400">Avg Split</div>
                            <div className="text-lg font-bold text-zinc-700" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                              {formatSplit(workout.avg_split)}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-xs font-bold uppercase tracking-wider text-zinc-400">Calories</div>
                            <div className="text-lg font-bold text-zinc-700" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                              {caloriesText(workout)}
                            </div>
                            {needsHrStrap(workout) && <div className="text-[10px] text-zinc-400" data-testid={`calories-needs-hr-${workout.workout_id}`}>Needs heart-rate strap</div>}
                          </div>
                          <div className="text-center">
                            <div className="text-xs font-bold uppercase tracking-wider text-zinc-400">Avg HR</div>
                            <div className="text-lg font-bold text-zinc-700" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                              {workout.avg_heart_rate ? `${workout.avg_heart_rate} bpm` : '—'}
                            </div>
                          </div>
                          <div className="text-center" data-testid={`card-max-hr-${workout.workout_id}`}>
                            <div className="text-xs font-bold uppercase tracking-wider text-zinc-400">Max HR</div>
                            <div className="text-lg font-bold text-zinc-700" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                              {workout.max_hr_bpm ? `${workout.max_hr_bpm} bpm` : '—'}
                            </div>
                          </div>
                        </div>
                        
                        {/* Slice C: one-tap export dropdown on every row. Doesn't
                            require the row to be expanded — power users can push
                            a session to Strava/Intervals.icu (or download TCX)
                            straight from the list. */}
                        <div onClick={(e) => e.stopPropagation()}>
                          <RowExportMenu
                            workout={workout}
                            stravaConnected={stravaConnected}
                            intervalsConnected={intervalsConnected}
                            onExport={exportWorkout}
                            testidPrefix="row-export"
                            size="sm"
                          />
                        </div>

                        {/* Expand button */}
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleExpand(workout.workout_id); }}
                          className="p-2 hover:bg-zinc-100 rounded-lg transition-colors"
                          data-testid={`expand-${workout.workout_id}`}
                          aria-label={isExpanded ? 'Collapse analysis' : 'Expand analysis'}
                          title={isExpanded ? 'Hide analysis' : 'Show analysis'}
                        >
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-zinc-500" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-zinc-500" />
                          )}
                        </button>
                      </div>
                      
                      {/* Mobile Metrics (visible on small screens) */}
                      <div className="md:hidden grid grid-cols-5 gap-2 mt-4">
                        <div className="bg-zinc-50 rounded-lg p-2 text-center">
                          <div className="text-xs font-bold text-zinc-400">Distance</div>
                          <div className="text-sm font-bold text-zinc-700">{formatDistanceMeters(workout.distance)}</div>
                        </div>
                        <div className="bg-zinc-50 rounded-lg p-2 text-center">
                          <div className="text-xs font-bold text-zinc-400">Avg Split</div>
                          <div className="text-sm font-bold text-zinc-700">{formatSplit(workout.avg_split)}</div>
                        </div>
                        <div className="bg-zinc-50 rounded-lg p-2 text-center">
                          <div className="text-xs font-bold text-zinc-400">Calories</div>
                          <div className="text-sm font-bold text-zinc-700">{caloriesText(workout)}</div>
                          {needsHrStrap(workout) && <div className="text-[10px] text-zinc-400">Needs heart-rate strap</div>}
                        </div>
                        <div className="bg-zinc-50 rounded-lg p-2 text-center">
                          <div className="text-xs font-bold text-zinc-400">Avg HR</div>
                          <div className="text-sm font-bold text-zinc-700">{workout.avg_heart_rate || '—'}</div>
                        </div>
                        <div className="bg-zinc-50 rounded-lg p-2 text-center" data-testid={`card-max-hr-mobile-${workout.workout_id}`}>
                          <div className="text-xs font-bold text-zinc-400">Max HR</div>
                          <div className="text-sm font-bold text-zinc-700">{workout.max_hr_bpm || '—'}</div>
                        </div>
                      </div>
                      <ZoneTimeBar workout={workout} />
                    </div>
                    
                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="border-t-2 border-zinc-100 bg-zinc-50 p-4" data-testid={`expanded-${workout.workout_id}`}>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2" data-testid={`hr-zone-method-${workout.workout_id}`}>
                          HR zones: {workout.hr_zone_method === 'karvonen' ? 'Karvonen (% of HR reserve)' : '% of max HR (earlier method)'}
                        </p>
                        <SessionMaxHr workout={workout} profileMaxHr={profileMaxHr} onProfileUpdated={setProfileMaxHr} />
                        {/* Session breakdown — CrewZ Session Data Model v1.6 §7.
                            Renders one strip per child session. Legacy rows without
                            child_sessions[] are collapsed into a single synthetic
                            child derived from splits_500m / program_name /
                            target_distance so the layout stays uniform. */}
                        <SessionBreakdown
                          workout={workout}
                          formatters={{ formatSplit, formatDurationFull, formatDistanceMeters }}
                          onExport={(w, platform) => exportWorkout(w || workout, platform)}
                          stravaConnected={stravaConnected}
                          intervalsConnected={intervalsConnected}
                        />

                        {/* Full Training Session Analysis (chart, map, tabs, recap, compare) */}
                        <SessionAnalysisInline workout={workout} />

                        {/* Export Buttons */}
                        <div className="space-y-3 mt-6">
                          {/* Primary: Download TCX */}
                          <button
                            onClick={() => exportWorkout(workout, 'fit')}
                            className="w-full px-4 py-2.5 bg-zinc-900 text-white rounded-lg font-bold text-sm hover:bg-zinc-800 transition-colors flex items-center justify-center gap-2"
                            data-testid={`export-fit-${workout.workout_id}`}
                          >
                            <Download className="w-4 h-4" />
                            Download TCX File
                          </button>
                          
                          {/* Secondary: Platform Uploads - Single Row */}
                          <div className="flex gap-2">
                            <button
                              onClick={() => exportWorkout(workout, 'garmin')}
                              className="flex-1 px-2 py-2 border-2 border-zinc-200 bg-white text-zinc-700 rounded-lg font-bold text-xs hover:bg-zinc-50 transition-colors flex flex-col items-center justify-center gap-1"
                              data-testid={`export-garmin-${workout.workout_id}`}
                              title="Garmin Connect"
                            >
                              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" fill="#007CC3"/>
                                <path d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z" fill="#007CC3"/>
                              </svg>
                              <span className="text-[10px]">Garmin</span>
                            </button>
                            <button
                              onClick={() => exportWorkout(workout, 'strava')}
                              className="flex-1 px-2 py-2 border-2 border-zinc-200 bg-white text-zinc-700 rounded-lg font-bold text-xs hover:bg-zinc-50 transition-colors flex flex-col items-center justify-center gap-1"
                              data-testid={`export-strava-${workout.workout_id}`}
                              title="Strava"
                            >
                              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#FC4C02">
                                <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.599h4.172L10.463 0l-7 13.828h4.169"/>
                              </svg>
                              <span className="text-[10px]">Strava</span>
                            </button>
                            <button
                              onClick={() => exportWorkout(workout, 'intervals')}
                              className="flex-1 px-2 py-2 border-2 border-zinc-200 bg-white text-zinc-700 rounded-lg font-bold text-xs hover:bg-zinc-50 transition-colors flex flex-col items-center justify-center gap-1"
                              data-testid={`export-intervals-${workout.workout_id}`}
                              title="Intervals.icu"
                            >
                              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                                <rect x="2" y="8" width="4" height="12" rx="1" fill="#6366F1"/>
                                <rect x="8" y="4" width="4" height="16" rx="1" fill="#8B5CF6"/>
                                <rect x="14" y="6" width="4" height="14" rx="1" fill="#A78BFA"/>
                                <rect x="20" y="10" width="2" height="10" rx="1" fill="#C4B5FD"/>
                              </svg>
                              <span className="text-[10px]">Intervals</span>
                            </button>
                            <button
                              onClick={() => exportWorkout(workout, 'trainingpeaks')}
                              className="flex-1 px-2 py-2 border-2 border-zinc-200 bg-white text-zinc-700 rounded-lg font-bold text-xs hover:bg-zinc-50 transition-colors flex flex-col items-center justify-center gap-1"
                              data-testid={`export-tp-${workout.workout_id}`}
                              title="TrainingPeaks"
                            >
                              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                                <path d="M12 2L2 9l10 7 10-7-10-7z" fill="#E31937"/>
                                <path d="M2 17l10 7 10-7M2 12l10 7 10-7" stroke="#E31937" strokeWidth="2" fill="none"/>
                              </svg>
                              <span className="text-[10px]">TP</span>
                            </button>
                            <button
                              onClick={() => exportWorkout(workout, 'googlefit')}
                              className="flex-1 px-2 py-2 border-2 border-zinc-200 bg-white text-zinc-700 rounded-lg font-bold text-xs hover:bg-zinc-50 transition-colors flex flex-col items-center justify-center gap-1"
                              data-testid={`export-googlefit-${workout.workout_id}`}
                              title="Google Fit"
                            >
                              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                                <path d="M12 2c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2s2-.9 2-2V4c0-1.1-.9-2-2-2z" fill="#4285F4"/>
                                <path d="M12 14c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2s2-.9 2-2v-4c0-1.1-.9-2-2-2z" fill="#EA4335"/>
                                <path d="M6 8c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2s2-.9 2-2v-4c0-1.1-.9-2-2-2z" fill="#FBBC05"/>
                                <path d="M18 8c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2s2-.9 2-2v-4c0-1.1-.9-2-2-2z" fill="#34A853"/>
                              </svg>
                              <span className="text-[10px]">G Fit</span>
                            </button>
                            <button
                              onClick={() => exportWorkout(workout, 'rowsandall')}
                              className="flex-1 px-2 py-2 border-2 border-zinc-200 bg-white text-zinc-700 rounded-lg font-bold text-xs hover:bg-zinc-50 transition-colors flex flex-col items-center justify-center gap-1"
                              data-testid={`export-rowsandall-${workout.workout_id}`}
                              title="RowsandAll"
                            >
                              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                                <path d="M4 12h16M4 12l4-4m-4 4l4 4" stroke="#0EA5E9" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <circle cx="20" cy="12" r="2" fill="#0EA5E9"/>
                              </svg>
                              <span className="text-[10px]">R&A</span>
                            </button>
                          </div>
                          
                          {/* Note about mock integrations */}
                          <p className="text-xs text-zinc-400 text-center">
                            Platform uploads are simulated. Real OAuth connections coming soon.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
